import type { FirebaseOptions } from 'firebase/app';

export type RuntimeEnvironment = 'development' | 'staging' | 'production';

export interface RuntimeConfig {
  environment: RuntimeEnvironment;
  firebase: FirebaseOptions;
  useLocalEmulators: boolean;
}

declare global {
  interface Window {
    FLATRATE_CONFIG_OVERRIDE?: {
      firebase?: Partial<FirebaseOptions>;
      useLocalEmulators?: boolean;
    };
  }
}

const DEFAULT_FIREBASE_CONFIG: FirebaseOptions = {
  apiKey: 'AIzaSyC217HkaYQZbzWZ6zJPXYjwOYSOCxbeDl8',
  authDomain: 'flatratepro-51334.firebaseapp.com',
  databaseURL: 'https://flatratepro-51334-default-rtdb.firebaseio.com/',
  projectId: 'flatratepro-51334',
  storageBucket: 'flatratepro-51334.firebasestorage.app',
  messagingSenderId: '444023456198',
  appId: '1:444023456198:web:73e25ffd357c5bd46e72c7'
};

const PLACEHOLDER_VALUES = new Set(['your-project-id', 'YOUR_LOCAL_API_KEY', 'your-project-id.firebaseapp.com']);

let runtimeConfigPromise: Promise<RuntimeConfig> | null = null;

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (!runtimeConfigPromise) {
    runtimeConfigPromise = resolveRuntimeConfig();
  }

  return runtimeConfigPromise;
}

async function resolveRuntimeConfig(): Promise<RuntimeConfig> {
  const environment = detectEnvironment();
  let firebase = { ...DEFAULT_FIREBASE_CONFIG };
  // Default to local emulators in development unless explicitly overridden.
  let useLocalEmulators = environment === 'development';

  if (window.FLATRATE_CONFIG_OVERRIDE?.firebase) {
    firebase = { ...firebase, ...window.FLATRATE_CONFIG_OVERRIDE.firebase };
  }
  if (typeof window.FLATRATE_CONFIG_OVERRIDE?.useLocalEmulators === 'boolean') {
    useLocalEmulators = window.FLATRATE_CONFIG_OVERRIDE.useLocalEmulators;
  }

  try {
    const response = await fetch('/firebase.config.local.json', { cache: 'no-store' });
    if (response.ok) {
      const payload = (await response.json()) as {
        firebase?: Partial<FirebaseOptions>;
        useLocalEmulators?: boolean;
      };
      const override = payload.firebase || {};
      if (!hasPlaceholderValues(override)) {
        firebase = { ...firebase, ...override };
      }
      if (typeof payload.useLocalEmulators === 'boolean') {
        useLocalEmulators = payload.useLocalEmulators;
      }
    }
  } catch {
    // Local override is optional.
  }

  return { environment, firebase, useLocalEmulators };
}

function detectEnvironment(): RuntimeEnvironment {
  const hostname = window.location.hostname;

  if (
    hostname === 'localhost' ||
    hostname.includes('127.0.0.1') ||
    hostname.includes('github.dev') ||
    hostname.includes('codespaces')
  ) {
    return 'development';
  }

  if (hostname.includes('staging') || hostname.includes('preview')) {
    return 'staging';
  }

  return 'production';
}

function hasPlaceholderValues(config: Partial<FirebaseOptions>): boolean {
  return Object.values(config).some(
    (value) => typeof value === 'string' && PLACEHOLDER_VALUES.has(value)
  );
}
